import React, { useState, useRef } from "react";
import { X, Mic, Image, Loader2, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { usePortalUpload, usePortalDetail } from "@/hooks/usePortal";
import { supabase } from "@/integrations/supabase/client";
import { useAuthContext } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { useAudioRecorder } from "@/hooks/useAudioRecorder";

interface EchoCapsuleModalProps {
  isOpen: boolean;
  onClose: () => void;
  portalId: string;
  onSave?: () => void;
}

const EchoCapsuleModal: React.FC<EchoCapsuleModalProps> = ({
  isOpen,
  onClose,
  portalId,
  onSave,
}) => {
  const { user } = useAuthContext();
  const [type, setType] = useState<"voice" | "photo">("voice");
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { uploadMedia, uploading } = usePortalUpload();
  
  const {
    isRecording: isVoiceRecording,
    recordingDuration: voiceDuration,
    audioBlob,
    startRecording: startVoiceRecording,
    stopRecording: stopVoiceRecording,
    resetRecording: resetVoiceRecording,
  } = useAudioRecorder();

  const handleSave = async () => {
    if (!user) return;

    if (type === "voice" && !audioBlob) {
      toast.error("Please record a voice note first");
      return;
    }
    if (type === "photo" && !fileInputRef.current?.files?.[0]) {
      toast.error("Please select a photo first");
      return;
    }

    setSaving(true);
    try {
      let contentUrl: string | null = null;

      // Upload media
      if (type === "voice" && audioBlob) {
        contentUrl = await uploadMedia(audioBlob as File, "voice");
      } else if (type === "photo" && fileInputRef.current?.files?.[0]) {
        // For photos, we need to upload to storage first
        const photoFile = fileInputRef.current.files[0];
        const fileName = `portal-echo-photos/${user.id}/${Date.now()}.jpg`;
        const { data, error } = await supabase.storage
          .from("portal-capsules")
          .upload(fileName, photoFile, { contentType: "image/jpeg" });

        if (error) throw error;
        const { data: { publicUrl } } = supabase.storage
          .from("portal-capsules")
          .getPublicUrl(fileName);
        contentUrl = publicUrl;
      }

      if (!contentUrl) {
        throw new Error("Failed to upload media");
      }

      // Save echo capsule to database with is_echo = true
      const { error: insertError } = await supabase
        .from("portal_capsules")
        .insert({
          portal_id: portalId,
          sender_user_id: user.id,
          type: type === "voice" ? "voice" : "prompt", // Photos stored as prompt type with content_url
          content_url: contentUrl,
          is_echo: true, // Mark as echo capsule
        });

      if (insertError) throw insertError;

      toast.success("Echo capsule saved to timeline! 💫");
      onSave?.();
      onClose();

      // Reset form
      setType("voice");
      resetVoiceRecording();
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } catch (error) {
      console.error("Error saving echo capsule:", error);
      toast.error("Failed to save echo capsule");
    } finally {
      setSaving(false);
    }
  };

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-foreground flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary" />
            Echo Capsule
          </h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X className="w-5 h-5" />
          </button>
        </div>

        <p className="text-sm text-muted-foreground mb-4">
          Pin a short voice note or photo to your Portal timeline
        </p>

        {/* Type Selection */}
        <div className="grid grid-cols-2 gap-2 mb-4">
          <button
            onClick={() => setType("voice")}
            className={cn(
              "p-4 rounded-xl border-2 transition-all",
              type === "voice"
                ? "border-primary bg-primary/5"
                : "border-gray-200 hover:border-primary/50"
            )}
          >
            <Mic className="w-6 h-6 mx-auto mb-2 text-primary" />
            <div className="text-sm font-medium">Voice</div>
          </button>
          <button
            onClick={() => setType("photo")}
            className={cn(
              "p-4 rounded-xl border-2 transition-all",
              type === "photo"
                ? "border-primary bg-primary/5"
                : "border-gray-200 hover:border-primary/50"
            )}
          >
            <Image className="w-6 h-6 mx-auto mb-2 text-primary" />
            <div className="text-sm font-medium">Photo</div>
          </button>
        </div>

        {/* Voice Recording */}
        {type === "voice" && (
          <div className="space-y-4 mb-4">
            {audioBlob ? (
              <div className="text-center p-4 rounded-xl bg-green-500/10 border border-green-500/20">
                <p className="text-sm font-medium text-green-700">Voice ready ({formatDuration(voiceDuration)})</p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={resetVoiceRecording}
                  className="mt-2"
                >
                  Re-record
                </Button>
              </div>
            ) : isVoiceRecording ? (
              <div className="text-center p-4 rounded-xl bg-red-500/10 border border-red-500/20">
                <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-2 animate-pulse">
                  <Mic className="w-8 h-8 text-red-500" />
                </div>
                <p className="text-lg font-bold text-red-600">{formatDuration(voiceDuration)}</p>
                <Button onClick={stopVoiceRecording} variant="destructive" className="mt-2">
                  Stop Recording
                </Button>
              </div>
            ) : (
              <Button onClick={startVoiceRecording} className="w-full">
                <Mic className="w-4 h-4 mr-2" />
                Start Recording
              </Button>
            )}
          </div>
        )}

        {/* Photo Upload */}
        {type === "photo" && (
          <div className="space-y-4 mb-4">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={() => {}}
            />
            <Button
              onClick={() => fileInputRef.current?.click()}
              variant="outline"
              className="w-full"
            >
              <Image className="w-4 h-4 mr-2" />
              Choose Photo
            </Button>
            {fileInputRef.current?.files?.[0] && (
              <img
                src={URL.createObjectURL(fileInputRef.current.files[0])}
                alt="Preview"
                className="w-full h-48 object-cover rounded-lg"
              />
            )}
          </div>
        )}

        {/* Save Button */}
        <Button
          onClick={handleSave}
          disabled={saving || (type === "voice" && !audioBlob) || (type === "photo" && !fileInputRef.current?.files?.[0])}
          className="w-full"
        >
          {saving ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Sparkles className="w-4 h-4 mr-2" />
              Pin to Timeline
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default EchoCapsuleModal;

